import { type ReactElement } from "react";
import { t } from "@lingui/core/macro";

import { BuildsetTooltip } from "_/components/buildsetTooltip";
import { Button } from "_/components/button";
import { DateTime } from "_/components/date-time";
import { FileDownloadLink } from "_/components/file-download-link";
import { JsonTree } from "_/components/json-tree";
import { MachineEditForm } from "_/components/machine-edit/machine-edit-form";
import { getBuildsetView } from "_/components/machine-listing/softwareVersionCell";
import { useModal } from "_/components/modal";
import { testPermissions } from "_/components/permission-test";
import { BlueLink, Link } from "_/components/router";
import { CustomCellStatic, Table, type ColumnDef } from "_/components/table";
import { Tooltip } from "_/components/tooltip";

import { useBuildsets, buildBuildsetIdMap } from "_/data/buildsets";
import { useChannelNameToBuildset } from "_/data/channels";
import {
  type BuildsetHistoryEntry,
  type Machine,
  useMachineBuildsetHistory,
  useMachineSnapshot,
} from "_/data/machines";
import { useOrg } from "_/data/orgs";
import { useCurrentUserPermissions, ROOT_DOMAIN } from "_/data/rbac";
import { useIsAonUser } from "_/data/users";

import { adminUrls } from "_/routes";

import { type Uuid } from "_/types";

import { formatBuildsetLabel } from "_/utils/buildset-display";

import { type MachineDetailsProps } from "./index";

import * as S from "./styled";

interface MachineSnapshotJsonProps {
  snapshot: ReturnType<typeof useMachineSnapshot>["data"];
  error: ReturnType<typeof useMachineSnapshot>["error"];
}

const MachineSnapshotJson = ({
  snapshot,
  error,
}: MachineSnapshotJsonProps): ReactElement => {
  if (error) {
    console.error("failure retrieving machine snapshot", error);
    return (
      <div>
        <span>{t`common.error`}:</span>
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </div>
    );
  }

  if (!snapshot) {
    return <></>;
  }

  const data = snapshot?.snapshot || {};

  if (!data) {
    return <div>{t`common.no-data`}</div>;
  }

  return (
    <div>
      <span>
        {t`components.machine-snapshot-json.updated-at`}:{" "}
        <DateTime
          /** Snapshot timestamp is in microseconds because it is from Klipper. */
          value={snapshot?.timestamp / 1000}
          defaultValue={t`common.unknown`}
        />
      </span>
      <JsonTree data={data} sortObjectKeys hideRoot />
    </div>
  );
};

const SoftwareVersion = ({ machine }: MachineDetailsProps) => {
  const { data: org } = useOrg(machine.orgId);
  const { data: userPermissions } = useCurrentUserPermissions(
    org?.domainId ?? ROOT_DOMAIN
  );
  const { data: rootPermissions } = useCurrentUserPermissions(ROOT_DOMAIN);
  const { data: buildsets = [] } = useBuildsets();
  const channelNameToBuildset = useChannelNameToBuildset();
  const buildsetIdMap = buildBuildsetIdMap(buildsets);
  const view = getBuildsetView({
    machine,
    channelNameToBuildset,
    buildsetIdMap,
  });
  const { buildsetPin } = machine;
  const updateInstructionsModal = useModal();

  const canReadSoftware = testPermissions("buildSets/read", userPermissions);
  const canLinkToBuildset = testPermissions("buildSets/read", rootPermissions);

  if (view.kind === "single") {
    const content = `${view.current.value}${buildsetPin ? ` (${t`common.pinned`})` : ""}`;
    const link = canLinkToBuildset ? (
      <Link to={adminUrls.buildset(view.current.id)}>{content}</Link>
    ) : (
      <>{content}</>
    );
    return <BuildsetTooltip id={view.current.tooltip}>{link}</BuildsetTooltip>;
  }
  if (view.kind === "pending") {
    const pendingBuildset = view.pending.id
      ? buildsetIdMap[view.pending.id]
      : undefined;
    const downloadUrl = pendingBuildset
      ? `/api/v0/buildsets/${pendingBuildset.id}/artifacts/${pendingBuildset.displayVersion}.raucb?ext=raucf`
      : undefined;

    return (
      <>
        <S.VersionWrapper>
          <BuildsetTooltip id={view.pending.tooltip}>
            <div className="pending">
              <span className="label">{t`common.pending`}: </span>
              {canLinkToBuildset && view.pending.id ? (
                <Link to={adminUrls.buildset(view.pending.id)}>
                  {view.pending.value}
                </Link>
              ) : (
                <span>{view.pending.value}</span>
              )}
              {canReadSoftware && downloadUrl && (
                <FileDownloadLink
                  href={downloadUrl}
                  variant="muted"
                  onClick={() => {
                    updateInstructionsModal.openModal(undefined);
                  }}
                  title={view.pending.value}
                />
              )}
            </div>
          </BuildsetTooltip>
          <div className="current">
            {canLinkToBuildset &&
            view.current.id &&
            view.current.value !== null ? (
              <>
                <span className="label">{t`common.current`}: </span>
                <Link to={adminUrls.buildset(view.current.id)}>
                  {view.current.value}
                </Link>
              </>
            ) : (
              <span>
                <span className="label">{t`common.current`}: </span>
                {view.current.value ?? "--"}
              </span>
            )}
          </div>
        </S.VersionWrapper>
        <updateInstructionsModal.Modal
          title={t`components.machine-details.update-modal-title`}
        >
          <S.ModalText>
            {t`components.machine-details.update-modal-instructions`}
          </S.ModalText>
          <S.ModalText>
            {t`components.machine-details.update-modal-details-prefix`}{" "}
            <BlueLink
              href="https://basis.aon3d.com/docs/hylo/operation/update_software#manual-updates-using-a-usb-drive"
              target="_blank"
              rel="noreferrer"
            >
              {t`components.machine-details.update-modal-details-link-text`}
            </BlueLink>
            .
          </S.ModalText>
        </updateInstructionsModal.Modal>
      </>
    );
  }
  return "--";
};

/** Open Fluidd (the console UI hosted on each machine) in a new browser tab. **/
const MachineConsoleLink = ({
  ipAddress,
}: {
  ipAddress: string | undefined;
}) =>
  ipAddress ? (
    <a target={"_blank"} rel="noreferrer" href={`http://${ipAddress}`}>
      {ipAddress}
    </a>
  ) : (
    "--"
  );

const versionHistoryColumns: ColumnDef<BuildsetHistoryEntry>[] = [
  {
    accessorKey: "timestamp",
    header: () => t`components.machine-version-history.first-observed`,
    meta: { customTd: true },
    cell: ({ row: { original: rowData } }) => (
      <CustomCellStatic>
        <Tooltip
          target={<DateTime value={rowData.timestamp} />}
          content={rowData.timestamp}
          placement="bottomStart"
          delay={200}
          wait={1000}
        />
      </CustomCellStatic>
    ),
  },
  {
    accessorKey: "displayVersion",
    header: () => t`common.software-version`,
    cell: ({ row: { original: rowData } }) => {
      const label = formatBuildsetLabel(rowData);
      return (
        <BuildsetTooltip
          id={label.id}
          value={label.value}
          fullName={label.fullName}
        >
          {label.value}
        </BuildsetTooltip>
      );
    },
  },
];

export function MachineVersionHistory({ machineId }: { machineId: Uuid }) {
  const {
    data: history = [],
    isPending,
    isError,
    isFetching,
    refetch,
  } = useMachineBuildsetHistory(machineId);

  const content = isError ? (
    <div role="alert">
      <p>{t`components.machine-version-history.error`}</p>
      <Button size="small" disabled={isFetching} onClick={() => void refetch()}>
        {t`components.machine-version-history.retry`}
      </Button>
    </div>
  ) : (
    <Table
      columns={versionHistoryColumns}
      data={history}
      loading={isPending}
      enableSorting={true}
      initialState={{ sorting: [{ id: "timestamp", desc: true }] }}
      getRowId={(entry) => `${entry.timestamp}-${entry.id}`}
      noDataLabel={t`components.machine-version-history.empty`}
    />
  );

  return (
    <S.NestedWidgetContainer>
      <S.Widget>
        <S.WidgetTitleContainer>
          <h2>{t`components.machine-version-history.title`}</h2>
        </S.WidgetTitleContainer>
        {content}
      </S.Widget>
      <S.Widget>
        <S.UseRemainingWidth />
      </S.Widget>
    </S.NestedWidgetContainer>
  );
}

export const MachineConfiguration = ({
  machine,
}: MachineDetailsProps): ReactElement => {
  const editModal = useModal<Machine>();
  const { data: org } = useOrg(machine.orgId);
  const { data: orgPermissions } = useCurrentUserPermissions(
    org?.domainId ?? ROOT_DOMAIN
  );
  const canEditMachine = testPermissions(
    "machines/writeProtected",
    orgPermissions
  );
  const isAonUser = useIsAonUser();
  const { data: snapshot, error } = useMachineSnapshot(machine.id);

  const detachedNote = machine.hardwareAttached ? (
    ""
  ) : (
    <S.MachineInfoLabel>{` (${t`common.detached`})`}</S.MachineInfoLabel>
  );

  return (
    <S.MachineOverviewContainer>
      <S.MachineInfo>
        <S.MachineConfigurationContainer>
          <S.HorizontalList>
            <S.MachineInfoItem>
              <S.MachineInfoLabel>{t`common.serial-number`}</S.MachineInfoLabel>
              <S.MachineInfoValue>
                {machine.hardware.serial}
                {detachedNote}
              </S.MachineInfoValue>
            </S.MachineInfoItem>
            <S.MachineInfoItem>
              <S.MachineInfoLabel>{t`common.machine-manufacture-date`}</S.MachineInfoLabel>
              <S.MachineInfoValue>
                {machine.hardware.manufactureDate}
              </S.MachineInfoValue>
            </S.MachineInfoItem>
          </S.HorizontalList>

          <S.MachineInfoItem>
            <S.MachineInfoLabelRow>
              <S.MachineInfoLabel>{t`common.software-version`}</S.MachineInfoLabel>
              {canEditMachine && (
                <S.IconLink
                  variant="Edit"
                  onClick={() => editModal.openModal(machine)}
                />
              )}
            </S.MachineInfoLabelRow>
            <S.MachineInfoValue>
              <SoftwareVersion machine={machine} />
            </S.MachineInfoValue>
          </S.MachineInfoItem>

          <S.HorizontalList>
            <S.MachineInfoItem>
              <S.MachineInfoLabel>
                {t`components.machine-header.lan-address`}
              </S.MachineInfoLabel>
              <S.MachineInfoValue>
                <MachineConsoleLink ipAddress={machine.lanAddress} />
              </S.MachineInfoValue>
            </S.MachineInfoItem>
            <S.MachineInfoItem>
              <S.MachineInfoLabel>
                {t`components.machine-header.wlan-address`}
              </S.MachineInfoLabel>
              <S.MachineInfoValue>
                <MachineConsoleLink ipAddress={machine.wlanAddress} />
              </S.MachineInfoValue>
            </S.MachineInfoItem>
          </S.HorizontalList>

          <S.HorizontalList>
            <S.MachineInfoItem>
              <S.MachineInfoLabel>{t`common.machine-id`}</S.MachineInfoLabel>
              <S.MachineInfoValue>
                <S.CopyableCode>{machine.id}</S.CopyableCode>
              </S.MachineInfoValue>
            </S.MachineInfoItem>
            <S.MachineInfoItem>
              <S.MachineInfoLabel>{t`common.hardware-id`}</S.MachineInfoLabel>
              <S.MachineInfoValue>
                <S.CopyableCode>{machine.hardware.id}</S.CopyableCode>
                {detachedNote}
              </S.MachineInfoValue>
            </S.MachineInfoItem>
          </S.HorizontalList>

          {isAonUser && (
            <S.MachineInfoItem>
              <S.MachineInfoLabel>{t`common.snapshot`}</S.MachineInfoLabel>
              <S.MachineInfoValue>
                {snapshot ? (
                  <MachineSnapshotJson snapshot={snapshot} error={error} />
                ) : (
                  "--"
                )}
              </S.MachineInfoValue>
            </S.MachineInfoItem>
          )}
        </S.MachineConfigurationContainer>
      </S.MachineInfo>
      <MachineVersionHistory machineId={machine.id} />
      <editModal.Modal title={t`components.admin-view.software-updates`}>
        {editModal.modalData && (
          <MachineEditForm
            machine={editModal.modalData}
            onClose={editModal.closeModal}
          />
        )}
      </editModal.Modal>
    </S.MachineOverviewContainer>
  );
};
